import React, { useEffect, useState } from 'react';
import { TransaksiPembayaran } from '../types/index.js';
import { Printer, X, CheckCircle, GraduationCap, Send, MessageSquare, CheckCheck, Phone, FileText } from 'lucide-react';
import { useDialog } from './ui/DialogProvider.js';
import { api } from '../services/api.js';
import { formatPaymentMethod, filterPokokItems, getPokokNominal } from '../utils/paymentMethod.js';

interface ReceiptModalProps {
  transaction: TransaksiPembayaran;
  onClose: () => void;
}

export const ReceiptModal: React.FC<ReceiptModalProps> = ({ transaction, onClose }) => {
  const { showAlert } = useDialog();
  const [waSending, setWaSending] = useState(false);
  const [waSent, setWaSent] = useState(Boolean(transaction.waNotificationSent));
  const [waPreview, setWaPreview] = useState<string>(transaction.waMessagePreview || '');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [recipientPhone, setRecipientPhone] = useState(transaction.waRecipientPhone || '');
  const [printFormat, setPrintFormat] = useState<'A4' | '58mm' | '80mm'>('A4');

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (!waPreview) {
      api.previewWhatsAppMessage(transaction.id || transaction.noTransaksi)
        .then((res) => {
          if (res?.data) {
            setWaPreview(res.data.message);
            if (!recipientPhone) {
              setRecipientPhone(res.data.recipientPhone || '');
            }
          }
        })
        .catch(() => {});
    }
  }, [transaction, waPreview, recipientPhone]);

  const handleSendWhatsApp = async () => {
    setWaSending(true);
    try {
      const res = await api.sendWhatsAppReceipt(transaction.id || transaction.noTransaksi, recipientPhone);
      if (res?.success) {
        setWaSent(true);
        if (res.data?.messagePreview) {
          setWaPreview(res.data.messagePreview);
        }
      }
    } catch (e) {
      console.error('Failed to send WA:', e);
    } finally {
      setWaSending(false);
    }
  };

  // Direct WhatsApp Web / App link as reliable fallback
  const getDirectWhatsAppUrl = () => {
    let phone = recipientPhone.replace(/[^0-9]/g, '');
    if (phone.startsWith('0')) phone = '62' + phone.slice(1);
    const text = encodeURIComponent(waPreview || `Bukti Pembayaran SDMT Ponorogo - No: ${transaction.noKuitansi}`);
    return `https://wa.me/${phone}?text=${text}`;
  };

  // Listen to Escape key to close dialog
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Helper convert number to indonesian words
  const terbilang = (angka: number): string => {
    const bilangan = [
      '', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan', 'Sepuluh', 'Sebelas'
    ];
    let kalimat = '';
    if (angka < 12) kalimat = bilangan[angka];
    else if (angka < 20) kalimat = terbilang(angka - 10) + ' Belas';
    else if (angka < 100) kalimat = terbilang(Math.floor(angka / 10)) + ' Puluh ' + terbilang(angka % 10);
    else if (angka < 200) kalimat = 'Seratus ' + terbilang(angka - 100);
    else if (angka < 1000) kalimat = terbilang(Math.floor(angka / 100)) + ' Ratus ' + terbilang(angka % 100);
    else if (angka < 2000) kalimat = 'Seribu ' + terbilang(angka - 1000);
    else if (angka < 1000000) kalimat = terbilang(Math.floor(angka / 1000)) + ' Ribu ' + terbilang(angka % 1000);
    else if (angka < 1000000000) kalimat = terbilang(Math.floor(angka / 1000000)) + ' Juta ' + terbilang(angka % 1000000);
    return kalimat.trim() + ' Rupiah';
  };

  // Sesuai instruksi: fee_aplikasi / biaya layanan TIDAK masuk kuitansi keuangan sekolah
  // Hanya tampilkan item tagihan pokok sekolah
  const displayItems = filterPokokItems(transaction.items || []);
  const finalTotal = displayItems.length > 0
    ? displayItems.reduce((acc, it) => acc + Number(it.nominal || 0), 0)
    : getPokokNominal(transaction);
  const cleanMethodName = formatPaymentMethod(transaction.metodeBayar, transaction.channel);

  return (
    <div
      id="receipt-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex flex-col items-center bg-black/65 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto cursor-pointer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden my-8 sm:my-12 animate-in fade-in zoom-in-95 cursor-default relative shrink-0"
      >
        {/* Header toolbar */}
        <div className="bg-sky-800 text-white px-4 sm:px-6 py-3 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
            <span className="font-bold text-xs sm:text-sm">Bukti Kuitansi Pembayaran Siswa</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={printFormat}
              onChange={(e) => setPrintFormat(e.target.value as any)}
              className="bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-lg px-2 py-1.5 outline-none cursor-pointer hidden sm:block"
            >
              <option value="A4" className="text-slate-800">Cetak A4 / Normal</option>
              <option value="58mm" className="text-slate-800">Thermal 58mm</option>
              <option value="80mm" className="text-slate-800">Thermal 80mm</option>
            </select>
            
            <button
              id="btn-print-receipt-top"
              type="button"
              onClick={handlePrint}
              title="Cetak Kuitansi"
              aria-label="Cetak kuitansi pembayaran"
              className="flex items-center gap-1.5 bg-white text-sky-900 hover:bg-sky-50 px-3 py-1.5 rounded-lg font-extrabold text-xs shadow-md transition cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden xs:inline">Cetak</span>
            </button>

            <button
              id="btn-close-receipt-top"
              type="button"
              onClick={onClose}
              title="Tutup (Esc)"
              aria-label="Tutup dialog kuitansi"
              className="flex items-center gap-1 bg-white/15 hover:bg-rose-600 active:bg-rose-700 text-white px-2.5 py-1.5 rounded-lg transition text-xs font-bold cursor-pointer border border-white/20"
            >
              <X className="w-4 h-4" />
              <span>Tutup</span>
            </button>
          </div>
        </div>

        {/* WhatsApp Notification Banner (Print Hidden) */}
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs print:hidden">
          <div className="flex items-center gap-2 text-emerald-950 font-medium min-w-0">
            <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <MessageSquare className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-emerald-900">Notifikasi WhatsApp Wali:</span>
                {waSent ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-200 text-emerald-900 text-[10px] font-extrabold px-1.5 py-0.5 rounded">
                    <CheckCheck className="w-3 h-3 text-emerald-700" />
                    Terkirim Otomatis
                  </span>
                ) : (
                  <span className="bg-amber-100 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded">
                    Siap Dikirim
                  </span>
                )}
              </div>
              <p className="text-[11px] text-emerald-800 truncate">
                Tujuan: <span className="font-mono font-bold">{recipientPhone || 'No. HP Siswa/Wali'}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowPreviewModal(true)}
              className="text-[11px] font-semibold text-emerald-800 hover:text-emerald-950 underline px-1 py-0.5 cursor-pointer"
            >
              Lihat Template Pesan
            </button>

            <a
              href={getDirectWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold px-2.5 py-1 rounded flex items-center gap-1 shadow-xs transition cursor-pointer"
            >
              <Send className="w-3 h-3" />
              <span>Buka di WA</span>
            </a>

            <button
              type="button"
              disabled={waSending}
              onClick={handleSendWhatsApp}
              className={`text-[11px] font-bold px-2.5 py-1 rounded flex items-center gap-1 transition shadow-xs cursor-pointer ${
                waSent
                  ? 'bg-slate-200 hover:bg-slate-300 text-slate-800'
                  : 'bg-emerald-700 hover:bg-emerald-800 text-white'
              }`}
            >
              {waSending ? 'Mengirim...' : waSent ? 'Kirim Ulang WA' : 'Kirim Sekarang'}
            </button>
          </div>
        </div>

        <style>
          {`
            @media print {
              @page {
                margin: 0;
                size: ${printFormat === 'A4' ? 'auto' : printFormat === '58mm' ? '58mm auto' : '80mm auto'};
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              
              html, body, #root, #root > div {
                height: auto !important;
                min-height: auto !important;
                overflow: visible !important;
                background: white !important;
                display: block !important;
                position: static !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              
              /* Hide all siblings of ReceiptModal to prevent blank pages */
              #root > div > *:not(#receipt-modal-backdrop) {
                display: none !important;
              }
              
              #receipt-modal-backdrop {
                position: static !important;
                display: block !important;
                background: transparent !important;
                padding: 0 !important;
                margin: 0 !important;
              }
              
              #receipt-modal-backdrop > div {
                position: static !important;
                display: block !important;
                transform: none !important;
                box-shadow: none !important;
                border: none !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                max-width: none !important;
              }
              
              .print\\:hidden, select {
                display: none !important;
              }

              ${printFormat !== 'A4' ? `
                #printable-receipt-thermal { display: block !important; padding: 4mm !important; }
                #printable-receipt-a4 { display: none !important; }
              ` : `
                #printable-receipt-thermal { display: none !important; }
                #printable-receipt-a4 { display: block !important; padding: 0 !important; }
              `}
            }
            ${printFormat !== 'A4' ? `#printable-receipt-thermal { display: block !important; } #printable-receipt-a4 { display: none !important; }` : ''}
          `}
        </style>

        {/* --- THERMAL RECEIPT LAYOUTS --- */}
        {printFormat !== 'A4' && (
          <div
            id="printable-receipt-thermal"
            className="bg-white text-black p-4 font-mono leading-tight mx-auto print:block"
            style={{ width: printFormat === '58mm' ? '58mm' : '80mm', display: 'none' }}
          >
            <div className="text-center mb-3">
              <h1 className="font-bold text-sm">SDMT PONOROGO</h1>
              <p className="text-[10px]">Jl. Ronowijayan No.01, Siman</p>
              <p className="text-[10px]">Telp: (0352) 481234</p>
            </div>
            
            <div className="text-[10px] mb-2 text-center border-y border-dashed border-black py-1">
              <p className="font-bold uppercase">Kuitansi Pembayaran</p>
              <p>{transaction.noTransaksi}</p>
              <p className="font-bold mt-1">
                {transaction.status === 'SUCCESS' ? '*** LUNAS ***' 
                 : transaction.status === 'PENDING' ? '*** BELUM LUNAS ***' 
                 : transaction.status === 'EXPIRED' ? '*** KADALUARSA ***' 
                 : `*** ${transaction.status} ***`}
              </p>
            </div>

            <div className="text-[10px] mb-2 space-y-0.5">
              <div className="flex"><span className="w-12">NAMA</span><span>: {transaction.namaSiswa}</span></div>
              <div className="flex"><span className="w-12">NIS</span><span>: {transaction.nis}</span></div>
              <div className="flex"><span className="w-12">KELAS</span><span>: {transaction.kelas}</span></div>
              <div className="flex"><span className="w-12">TGL</span><span>: {new Date(transaction.tanggal).toLocaleDateString('id-ID')}</span></div>
              <div className="flex"><span className="w-12">METODE</span><span>: {cleanMethodName}</span></div>
            </div>

            <div className="border-t border-dashed border-black pt-1 mt-1 text-[10px]">
              {displayItems && displayItems.length > 0 ? (
                displayItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between mb-1">
                    <span className="truncate pr-2 flex-1">
                      {item.namaTagihan || ((item as any).tagihanDetail?.namaPos + ' ' + ((item as any).tagihanDetail?.bulan || ''))}
                    </span>
                    <span className="font-bold text-right">{item.nominal.toLocaleString('id-ID')}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between mb-1">
                  <span className="truncate pr-2 flex-1">PEMBAYARAN</span>
                  <span className="font-bold text-right">{finalTotal.toLocaleString('id-ID')}</span>
                </div>
              )}
            </div>

            <div className="border-t border-dashed border-black pt-1 mt-1 text-[10px] font-bold">
              <div className="flex justify-between">
                <span>TOTAL</span>
                <span>{finalTotal.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between">
                <span>DIBAYAR</span>
                <span>{finalTotal.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between">
                <span>KEMBALI</span>
                <span>0</span>
              </div>
            </div>

            <div className="text-center text-[9px] mt-4 pt-2 border-t border-dashed border-black">
              <p>Terima Kasih</p>
              <p>Simpan struk ini sebagai bukti pembayaran yang sah.</p>
            </div>
          </div>
        )}

        {/* --- STANDARD A4 LAYOUT --- */}
        <div className={`p-5 sm:p-8 text-slate-800 bg-white ${printFormat !== 'A4' ? 'hidden' : 'block'}`} id="printable-receipt-a4">
          {/* School Header */}
          <div className="border-b-2 border-slate-800 pb-4 mb-4 flex flex-wrap sm:flex-nowrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-sky-900 text-amber-400 rounded-full flex items-center justify-center font-black text-lg sm:text-xl shadow-md border-2 border-amber-400 shrink-0">
                SDMT
              </div>
              <div>
                <h1 className="font-extrabold text-base sm:text-lg uppercase tracking-tight text-slate-900">
                  SD MUHAMMADIYAH TERPADU PONOROGO
                </h1>
                <p className="text-[11px] sm:text-xs text-slate-600">
                  Jl. Ronowijayan No. 01, Siman, Ponorogo, Jawa Timur | Telp: (0352) 481234
                </p>
                <p className="text-[10px] sm:text-[11px] text-sky-800 font-semibold">
                  Unit Keuangan & Sistem Pembayaran Siswa Terpadu (Madinapay Engine)
                </p>
              </div>
            </div>

            <div className="text-left sm:text-right shrink-0">
              {transaction.status === 'SUCCESS' ? (
                <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-1 rounded border border-emerald-300">
                  LUNAS / PAID
                </span>
              ) : transaction.status === 'PENDING' ? (
                <span className="inline-block bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded border border-amber-300">
                  MENUNGGU PEMBAYARAN
                </span>
              ) : transaction.status === 'EXPIRED' ? (
                <span className="inline-block bg-rose-100 text-rose-800 text-xs font-bold px-2.5 py-1 rounded border border-rose-300">
                  KADALUARSA / EXPIRED
                </span>
              ) : (
                <span className="inline-block bg-slate-100 text-slate-800 text-xs font-bold px-2.5 py-1 rounded border border-slate-300">
                  {transaction.status}
                </span>
              )}
              <p className="text-xs font-mono font-bold text-slate-800 mt-1">{transaction.noKuitansi}</p>
            </div>
          </div>

          {/* Title */}
          <div className="text-center my-3">
            <h2 className="text-sm sm:text-base font-bold uppercase tracking-wider text-slate-900 underline decoration-2">
              KUITANSI PEMBAYARAN SISWA
            </h2>
            <p className="text-xs text-slate-500 font-mono">No. Transaksi: {transaction.noTransaksi}</p>
          </div>

          {/* Student & Payment Metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs bg-slate-50 p-3.5 sm:p-4 rounded-lg border border-slate-200 my-4">
            <div className="space-y-1.5">
              <div className="flex">
                <span className="w-28 text-slate-500">Nama Siswa</span>
                <span className="font-bold text-slate-900">: {transaction.namaSiswa}</span>
              </div>
              <div className="flex">
                <span className="w-28 text-slate-500">NIS</span>
                <span className="font-semibold text-slate-800">: {transaction.nis}</span>
              </div>
              <div className="flex">
                <span className="w-28 text-slate-500">Kelas</span>
                <span className="text-slate-800">: {transaction.kelas}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex">
                <span className="w-28 text-slate-500">Tgl. Pembayaran</span>
                <span className="font-semibold text-slate-800">: {new Date(transaction.tanggal).toLocaleString('id-ID', { dateStyle: 'long', timeStyle: 'short' })}</span>
              </div>
              <div className="flex">
                <span className="w-28 text-slate-500">Kasir / Penerima</span>
                <span className="text-slate-800">: {transaction.kasir}</span>
              </div>
              <div className="flex">
                <span className="w-28 text-slate-500">Metode Pembayaran</span>
                <span className="font-bold text-sky-800 uppercase">: {cleanMethodName}</span>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <table className="w-full text-xs border border-slate-300 rounded overflow-hidden my-4">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-300">
                <th className="py-2 px-3 text-left w-10">No</th>
                <th className="py-2 px-3 text-left">Deskripsi Tagihan</th>
                <th className="py-2 px-3 text-left">Pos Pembayaran</th>
                <th className="py-2 px-3 text-right">Nominal (Rp)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {displayItems.map((it, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="py-2 px-3 text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-3 font-semibold text-slate-900">{it.namaTagihan || (it as any).tagihanDetail?.namaPos || '-'}</td>
                  <td className="py-2 px-3 text-slate-600">{it.posName || '-'}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                    Rp {Number(it.nominal || 0).toLocaleString('id-ID')}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-sky-50 font-bold border-t-2 border-slate-300 text-slate-900">
                <td colSpan={3} className="py-2.5 px-3 text-right uppercase tracking-wider">
                  Total Dibayar:
                </td>
                <td className="py-2.5 px-3 text-right font-mono text-sm text-sky-900">
                  Rp {finalTotal.toLocaleString('id-ID')}
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Terbilang Box */}
          <div className="bg-slate-100 p-3 rounded text-xs border border-slate-200 my-4">
            <span className="font-bold text-slate-600">Terbilang: </span>
            <span className="italic font-semibold text-slate-800">"{terbilang(finalTotal)}"</span>
          </div>

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-8 text-xs pt-6 mt-4 border-t border-slate-200">
            <div className="text-center">
              <p className="text-slate-500">Penyetor / Wali Murid</p>
              <div className="h-16"></div>
              <p className="font-bold text-slate-800 border-t border-slate-300 inline-block px-6 pt-1">
                ( ..................................... )
              </p>
            </div>
            <div className="text-center">
              <p className="text-slate-500">Ponorogo, {new Date(transaction.tanggal).toLocaleDateString('id-ID')}</p>
              <p className="text-slate-500">Petugas Kasir / Loket</p>
              <div className="h-16 flex items-center justify-center">
                {transaction.status === 'SUCCESS' ? (
                  <div className="border border-emerald-500 text-emerald-600 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded rotate-[-8deg] opacity-75">
                    LUNAS SDMT
                  </div>
                ) : transaction.status === 'PENDING' ? (
                  <div className="border border-amber-500 text-amber-600 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded rotate-[-8deg] opacity-75">
                    UNPAID
                  </div>
                ) : transaction.status === 'EXPIRED' ? (
                  <div className="border border-rose-500 text-rose-600 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded rotate-[-8deg] opacity-75">
                    KADALUARSA
                  </div>
                ) : (
                  <div className="border border-slate-500 text-slate-600 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded rotate-[-8deg] opacity-75">
                    {transaction.status}
                  </div>
                )}
              </div>
              <p className="font-bold text-slate-800 border-t border-slate-300 inline-block px-6 pt-1">
                {transaction.kasir}
              </p>
            </div>
          </div>

          {/* Note */}
          <p className="text-[10px] text-slate-400 text-center mt-6">
            * Simpan kuitansi ini sebagai bukti pembayaran yang sah. Dicetak otomatis oleh Sistem Pembayaran Terpadu SDMT Ponorogo.
          </p>
        </div>

        {/* Bottom Sticky/Action Toolbar (Hidden when printing) */}
        <div className="bg-slate-100 border-t border-slate-200 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <div className="text-xs text-slate-500 hidden sm:block">
            Tekan <kbd className="px-1.5 py-0.5 bg-white text-slate-700 rounded text-[11px] font-mono border border-slate-300 shadow-2xs">Esc</kbd> atau klik tombol di samping untuk menutup.
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            <button
              id="btn-close-receipt-bottom"
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 active:bg-slate-400 text-slate-700 hover:text-slate-900 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <X className="w-4 h-4" />
              <span>Tutup Kuitansi</span>
            </button>

            <button
              id="btn-print-receipt-bottom"
              type="button"
              onClick={handlePrint}
              className="px-4 py-2 bg-sky-700 hover:bg-sky-600 active:bg-sky-800 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <Printer className="w-4 h-4" />
              <span>Cetak Kuitansi</span>
            </button>
          </div>
        </div>

        {/* WhatsApp Preview Popup Modal */}
        {showPreviewModal && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-slate-300 overflow-hidden animate-in fade-in">
              <div className="bg-emerald-700 text-white px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="font-bold text-xs">Template Pesan WhatsApp Otomatis</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(false)}
                  className="text-white hover:text-emerald-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-600">Nomor Penerima:</span>
                  <input
                    type="text"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="Contoh: 081234567890"
                    className="flex-1 px-2.5 py-1 text-xs border rounded-lg font-mono text-slate-800 focus:outline-emerald-600"
                  />
                </div>

                <div className="bg-slate-900 text-slate-100 p-3.5 rounded-lg text-[11px] font-mono whitespace-pre-wrap max-h-72 overflow-y-auto leading-relaxed border border-slate-700">
                  {waPreview}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(waPreview).then(() => {
                        showAlert('Template pesan disalin ke clipboard!', { type: 'success', title: 'Berhasil Disalin' });
                      });
                    }}
                    className="text-xs text-slate-600 hover:text-slate-900 underline font-semibold cursor-pointer"
                  >
                    Salin Teks
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPreviewModal(false)}
                      className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded text-xs font-semibold cursor-pointer"
                    >
                      Tutup
                    </button>
                    <button
                      type="button"
                      disabled={waSending}
                      onClick={async () => {
                        await handleSendWhatsApp();
                        setShowPreviewModal(false);
                      }}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <span>{waSending ? 'Mengirim...' : 'Kirim Sekarang'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
